<template>
  <div>
    <li>
      <fieldset>
        <legend>
          <span class="h3">Your contact details</span>
        </legend>
        <ul class="questions">
          <li>
            <InputWithError
              v-model="form.applicantFirstName"
              id="applicantFirstName"
              name="Applicant First Name"
              label="First name"
            />
          </li>
        </ul>
      </fieldset>
    </li>
    <li>
      <fieldset>
        <ul class="questions">
          <li>
            <label for="Applicant Middle Name">
              <span class="label">Middle Name(s)</span></label
            >
            <input
              v-model="form.applicantMiddleName"
              name="Applicant Middle Name"
              type="text"
              class="stretchWidth"
              maxlength="255"
            />
          </li>
        </ul>
      </fieldset>
    </li>
    <li>
      <fieldset>
        <ul class="questions">
          <li>
            <InputWithError
              v-model="form.applicantFamilyName"
              id="applicantFamilyName"
              name="Applicant Family Name"
              label="Family name"
              hint="If only one name, enter n/a."
            />
          </li>
        </ul>
      </fieldset>
    </li>
    <li>
      <fieldset>
        <ul class="questions">
          <li class="applicantPreferredContactEmail">
            <InputWithError
              v-model="form.applicantPreferredContactEmail"
              id="applicantPreferredContactEmail"
              name="Applicant Preferred Contact Email"
              label="Email"
              type="email"
            />
          </li>
          <ValidationProvider rules="required:#label-confirmEmail">
            <li class="applicantPreferredContactEmail">
              <label :for="confirmEmail" :id="label-confirmEmail">
                <span class="label">Confirm email</span>
                <abbr class="required" title="(required)">*</abbr>
                <em class="error-msg alert" v-if="confirmEmail != form.applicantPreferredContactEmail">Emails entered must match</em>
              </label>
              <input
                id="confirmEmail"
                v-model="confirmEmail"
                name="Confirm email"
                type="text"
                maxlength="255"
                size="100"
                :class="{'error-input': confirmEmail != form.applicantPreferredContactEmail, 'stretchWidth': true}"
              />
            </li>
          </ValidationProvider>
          <li class="applicantPreferredContactMobile">
            <InputWithError
              v-model="form.applicantPreferredContactMobile"
              id="applicantPreferredContactMobile"
              name="Applicant Preferred Contact Mobile"
              type="tel"
              hint="Mobile preferred"
              label="Contact number"
            />
          </li>
        </ul>
      </fieldset>
    </li>
    <li>
      <fieldset>
        <span class="h3">Your home address at the time of the disaster</span>
        <div class="status info">
          <p>
            You will need to provide evidence that this was your address at the
            time of the disaster.
          </p>
        </div>
        <ul class="questions">
          <li>
            <InputWithError
              v-model="form.applicantDisasterAddressLine1"
              id="applicantDisasterAddressLine1"
              name="Applicant Disaster Address Line 1"
              label="Address line 1"
            />
          </li>
          <li>
            <label for="applicantDisasterAddressLine2">
              <span class="label">Address line 2</span></label
            >
            <input
              id="applicantDisasterAddressLine2"
              autocomplete="address-line2"
              v-model="form.applicantDisasterAddressLine2"
              name="attributes.applicantDisasterAddressLine2"
              type="text"
              class="stretchWidth"
              maxlength="255"
            />
          </li>
          <li>
            <label for="applicantDisasterAddressLine3">
              <span class="label">Address line 3</span></label
            >
            <input
              id="applicantDisasterAddressLine3"
              autocomplete="address-line3"
              v-model="form.applicantDisasterAddressLine3"
              name="attributes.applicantDisasterAddressLine3"
              type="text"
              class="stretchWidth"
              maxlength="255"
            />
          </li>
          <li class="group">
            <ol class="questions compact">
              <li>
                <InputWithError
                  v-model="form.applicantDisasterTownCitySuburb"
                  id="applicantDisasterTownCitySuburb"
                  name="Applicant Disaster Suburb"
                  label="Town or city"
                  maxlength="25"
                  size="15"
                />
              </li>
              <li>
                <InputWithError
                  v-model="form.applicantDisasterState"
                  id="applicantDisasterState"
                  name="Applicant Disaster State"
                  label="State or territory"
                  maxlength="25"
                  size="10"
                />
              </li>
              <li>
                <InputWithError
                  v-model="form.applicantDisasterPostcode"
                  id="applicantDisasterPostcode"
                  name="Applicant Disaster Postcode"
                  label="Postcode"
                  maxlength="10"
                  size="10"
                />
              </li>
            </ol>
          </li>
        </ul>
      </fieldset>
    </li>
    <li>
      <fieldset>
        <legend for="is-address-same">
          <span class="label" id="label-is-address-same"
            >Is your current postal address the same as the home address
            above?</span
          >
          <abbr class="required" title="(required)">*</abbr>
        </legend>
        <ul class="choices compact">
          <li>
            <ValidationProvider rules="required:#label-is-address-same">
              <input
                v-model="form.isAddressSame"
                id="is-address-same-yes"
                value="true"
                name="Is Address Same"
                type="radio"
              />
            </ValidationProvider>
            <label for="is-address-same-yes">Yes</label>
          </li>
          <li>
            <input
              v-model="form.isAddressSame"
              id="is-address-same-no"
              value="false"
              name="Is Address Same"
              type="radio"
            /><label for="is-address-same-no">No</label>
          </li>
        </ul>
      </fieldset>
    </li>
    <li v-if="form.isAddressSame == 'false'">
      <fieldset>
        <ul class="questions">
          <label for="applicantHomeAddress">
            <span class="h3">Your current postal address</span>
          </label>
          <li>
            <InputWithError
              v-model="form.applicantHomeAddressLine1"
              id="applicantHomeAddressLine1"
              name="Applicant Home Address Line 1"
              label="Address line 1"
            />
          </li>
          <li>
            <label for="applicantHomeAddressLine2">
              <span class="label">Address line 2</span></label
            >
            <input
              id="applicantHomeAddressLine2"
              autocomplete="address-line2"
              v-model="form.applicantHomeAddressLine2"
              name="attributes.applicantHomeAddressLine2"
              type="text"
              class="stretchWidth"
              maxlength="255"
            />
          </li>
          <li>
            <label for="applicantHomeAddressLine3">
              <span class="label">Address line 3</span></label
            >
            <input
              id="applicantHomeAddressLine3"
              autocomplete="address-line3"
              v-model="form.applicantHomeAddressLine3"
              name="attributes.applicantHomeAddressLine3"
              type="text"
              class="stretchWidth"
              maxlength="255"
            />
          </li>
          <li class="group">
            <ol class="questions compact">
              <li>
                <InputWithError
                  v-model="form.applicantHomeTownCitySuburb"
                  id="applicantHomeTownCitySuburb"
                  name="Applicant Home Suburb"
                  label="Town or city"
                  maxlength="25"
                  size="15"
                />
              </li>
              <li>
                <InputWithError
                  v-model="form.applicantHomeState"
                  id="applicantHomeState"
                  name="Applicant Home State"
                  label="State or territory"
                  maxlength="25"
                  size="10"
                />
              </li>
              <li>
                <InputWithError
                  v-model="form.applicantHomePostcode"
                  id="applicantHomePostcode"
                  name="Applicant Home Postcode"
                  label="Postcode"
                  maxlength="10"
                  size="10"
                />
              </li>
            </ol>
          </li>
        </ul>
      </fieldset>
    </li>
  </div>
</template>

<script>
import InputWithError from "../../InputWithError.vue";

export default {
  props: {
    value: Object,
  },
  data() {
    return {
      confirmEmail: ''
    };
  },
  computed: {
    form: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  components: {
    InputWithError,
  },
};
</script>
